import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { log } from '@/lib/logger';
import type { BillingPlanInfo } from '@overlayqa/shared';

import { fetchApi } from '../lib/api/client';
import { useRetainedLoadError } from './useRetainedLoadError';

/**
 * Fetch the publicly-visible Clerk billing plans (Starter, Team, Pro)
 * with their real prices and features. The server proxies clerk.billing.getPlanList
 * and caches for 10 minutes. Use this anywhere the dashboard needs to render
 * plan cards without hardcoding prices/features (which drift from Clerk).
 *
 * Returns plans in whatever order Clerk returns them — the consumer should
 * sort by their own ordering preference (e.g., by monthly fee).
 */
export function useBillingPlans() {
  const { getToken } = useAuth();
  const query = useQuery<BillingPlanInfo[]>({
    queryKey: ['billing', 'plans'],
    queryFn: async () => {
      log.info('[useBillingPlans] fetching');
      const token = await getToken();
      return fetchApi<BillingPlanInfo[]>('/billing/plans', { token: token ?? undefined });
    },
    // Plans rarely change. Keep them in cache long; the server already
    // caches the upstream Clerk call for 10 minutes.
    staleTime: 5 * 60 * 1000,
  });
  const error = useRetainedLoadError(query.error, query.isFetching, 'billing-plans');
  return { ...query, error, isLoading: query.isLoading && !error };
}
